import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { VALUES, ABOUT_IMAGE } from "@/lib/data";
import { Seo } from "@/components/Seo";

const FADE_LEFT = {
    initial: { opacity: 0, x: -20 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 },
};

const FADE_UP_DELAYED = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, delay: 0.1 },
};

const VALUE_ANIM = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
};

export default function WhoWeAre() {
    return (
        <main data-testid="who-we-are-page" className="bg-bone">
            <Seo
                title="Who We Are — Justin Brazier, Licensed Builder Central Coast | Brazier Building & Design"
                description="Meet Justin Brazier — licensed builder (Lic 317424c) with 30+ years of hands-on experience building custom homes, renovations and extensions on the Central Coast NSW."
                path="/who-we-are"
            />
            <section className="relative pt-20 pb-10 md:pt-28 md:pb-16 bg-bone2">
                <div className="container-wide">
                    <div className="overline mb-5">Who we are</div>
                    <h1 className="font-display text-6xl md:text-8xl font-bold tracking-tight leading-[1.02] max-w-5xl">
                        Central Coast <span className="italic text-clay">local.</span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-char/70 max-w-2xl">
                        Still building across Newcastle, the Central Coast and Sydney.
                    </p>
                </div>
            </section>

            {/* Founder */}
            <section className="section-pad">
                <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <motion.div
                        {...FADE_LEFT}
                        className="lg:col-span-5"
                    >
                        <div className="aspect-[4/5] overflow-hidden">
                            <img src={ABOUT_IMAGE} alt="Justin Brazier, founder" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </motion.div>

                    <motion.div
                        {...FADE_UP_DELAYED}
                        className="lg:col-span-7"
                    >
                        <div className="overline mb-5">Founder</div>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight mb-6">
                            Meet Justin Brazier.
                        </h2>
                        <div className="space-y-5 text-ink2 text-base md:text-lg leading-relaxed">
                            <p>
                                Justin is the Director and Licensed Builder behind Brazier Building &amp; Design. Based at Forresters Beach, he's spent over 30 years building homes across the Central Coast and Newcastle.
                            </p>
                            <p>
                                Justin is hands-on, on site, and personally involved in every project — from the first conversation to the final walkthrough. We work closely with Lynne Hardy of Adoro Cor Design on interiors, so design and construction stay in step from day one.
                            </p>
                            <p>
                                Experienced. Refined. Honest. We let the work speak for itself.
                            </p>
                        </div>
                        <div className="mt-10">
                            <Link to="/contact" className="btn-primary" data-testid="wwa-contact-cta">
                                Work with us
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Values */}
            <section className="section-pad bg-bone2">
                <div className="container-wide">
                    <div className="max-w-3xl mb-14">
                        <div className="overline mb-5">What we stand for</div>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
                            Our values.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.title}
                                {...VALUE_ANIM}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                data-testid={`value-${v.title.toLowerCase()}`}
                                className="bg-bone p-8 md:p-12"
                            >
                                <div className="font-display text-5xl md:text-6xl text-clay font-extralight mb-5">
                                    0{i + 1}
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl font-light mb-3">{v.title}</h3>
                                <p className="text-ink2 leading-relaxed">{v.blurb}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
